import type { MenuOption } from "@dealer-center/core-components";
import { Box, useInterstateToken } from "@dealer-center/core-components";
import { BottomBar } from "./bottomBar";
import type {
  NavigationMenuItemInterface,
  UserMenuItemsInterface
} from "@dealer-center/ddc-api-commoncontainer-interface";
import { TopBar } from "./top-bar";
import { useNavBarStore } from "./store";
import { useEffect } from "react";
import {
  LoadingIndicator,
  useWindowDimensions
} from "@dealer-center/core-custom-components";
import { LinksMenuNew } from "./top-bar/navigation/links-menu-new";
import type { OnSearchInterface } from "./top-bar/search/SearchBar";
import {
  calculateVisibility,
  createComponents,
  hasAnyVisibilityChanged
} from "./page-template-components";

export interface PageTemplateProps {
  loading?: boolean;
  linksMenu?: NavigationMenuItemInterface[];
  user?: UserMenuItemsInterface;
  nav?: NavigationMenuItemInterface[];
  hasBridgeBar?: boolean;
  availableAccounts?: MenuOption[];
  selectAccount?: (account: string) => void;
  selectedAccount?: string;
  selectedAccountStatus?: string;
  children: React.ReactNode;
  onSearch?: (value: OnSearchInterface) => void;
  hideSidebar?: boolean; // Add new prop to hide sidebar completely
  isLoadingSidebar?: boolean; // Add new prop to show loading state for sidebar
}

export const PageTemplate = ({
  linksMenu,
  loading,
  user,
  nav,
  availableAccounts,
  selectedAccount,
  selectedAccountStatus,
  selectAccount,
  hasBridgeBar = true,
  onSearch,
  hideSidebar = false, // Default to false
  isLoadingSidebar = false, // Default to false
  children
}: PageTemplateProps) => {
  const windowDimensions = useWindowDimensions();
  const getToken = useInterstateToken();
  const { setInitalState, setVisibility, visibility } = useNavBarStore();

  // Set initial state for the navigation bar store
  useEffect(() => {
    setInitalState({
      linksMenu,
      user,
      nav,
      hasBridgeBar,
      availableAccounts,
      setSelectedAccountId: selectAccount,
      selectedAccountName: selectedAccount,
      selectedAccountStatus,
      onSearch
    });
  }, [
    onSearch,
    availableAccounts,
    hasBridgeBar,
    linksMenu,
    nav,
    selectAccount,
    selectedAccount,
    selectedAccountStatus,
    setInitalState,
    user
  ]);

  // Custom hook to manage visibility settings
  useEffect(() => {
    const newVisibility = calculateVisibility(windowDimensions);
    const hasVisibilityChanged = hasAnyVisibilityChanged(
      newVisibility,
      visibility
    );
    if (!hasVisibilityChanged) return;
    setVisibility(newVisibility);
  }, [setVisibility, visibility, windowDimensions]);

  // Show loading indicator if loading is true
  if (loading) {
    return <LoadingIndicator actionName="Loading Page" />;
  }

  const components = createComponents(getToken);

  // Sidebar component - only show if not hidden and visibility allows it
  // Show loading indicator if sidebar is loading
  const sideBar = !hideSidebar && visibility.pinLinksMenu && (
    <Box
      data-testid={components.leftSidebar.testId}
      style={components.leftSidebar.style}
    >
      <aside>
        {isLoadingSidebar ? (
          <LoadingIndicator actionName="Loading Sidebar" />
        ) : (
          <LinksMenuNew closeNavbar={() => undefined} />
        )}
      </aside>
    </Box>
  );

  const footer = visibility.showFooter && (
    <Box
      data-testid={components.footerSection.testId}
      sx={components.footerSection.style}
    >
      <BottomBar />
    </Box>
  );

  return (
    <Box
      data-testid={components.pageTemplate.testId}
      sx={components.pageTemplate.style}
    >
      <Box data-testid={components.header.testId} sx={components.header.style}>
        <TopBar />
      </Box>
      <Box
        data-testid={components.content.testId}
        sx={components.content.style}
      >
        {sideBar}
        <Box
          data-testid={components.rightContent.testId}
          sx={components.rightContent.style}
        >
          <Box data-testid={components.page.testId} sx={components.page.style}>
            <Box
              data-testid={components.pageContent.testId}
              style={{
                ...components.pageContent.style,
                // Remove padding if sidebar is hidden (for custom layouts like merchandising)
                ...(hideSidebar && { padding: 0, maxWidth: "none" })
              }}
            >
              <main>{children}</main>
            </Box>
          </Box>
        </Box>
      </Box>
      {footer}
    </Box>
  );
};
